// POIButton — kleiner Pin-Button unten in der BottomRightStack-Reihe, der ein
// Panel aufklappt. Im Panel: Toggles pro POI-Kategorie.
//
// Wave 14cf-fix15: alle 6 Kategorien haben jetzt ihre eigenen Toggles.
// Vorher war nur drinking_water togglebar; cafe/pranzo/bike_shop/beach/
// viewpoint wurden "always-on" gerendert (s. fix10-Kommentar). User-Feedback:
// "POI angelegt — nicht auf der Karte; Kategorie Cafés nicht im Toggle-Panel"
// → User erwartet pro Kategorie einen Toggle. Cache-Hint: nach dem Aufmachen
// des Panels triggert ein 'adt:poi-refresh'-Event den MapPage-Reloader, damit
// frisch angelegte Server-POIs sofort sichtbar werden (cache war 1h fresh).
//
// Struktur:
//   - Button selbst: 26 px hoch, bg-black/60, identisch zur DifficultyLegend.
//   - Panel: aufklappend, mit Backdrop dahinter (fixed inset-0 bg-black/80,
//     Klick schließt). Wave 14cf-fix15: Backdrop dunkler (war 60), damit
//     der Ride-Button (z-30) hinter dem Panel weniger durchscheint.

import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { poiOverrideBus } from '../lib/poiOverrideBus';
import {
  usePoiDrinkingWater,
  usePoiBatteryStation,
  useOverpassPoiToggles,
} from '../lib/usePois';
import { useVlMembership } from '../lib/vlMembership';
import {
  POI_CATEGORIES,
  OVERPASS_EXTRA_CATEGORIES,
  getPoiCacheInfo,
  type PoiCategoryId,
} from '../lib/poi';

/**
 * @param embedded Rendert den Trigger ohne eigenes Hintergrund-Chrome, damit
 *   er Teil der vereinheitlichten Bottom-Pill (MapPage.BottomBar) werden
 *   kann. Modal bleibt identisch.
 */
export function POIButton({ embedded = false }: { embedded?: boolean } = {}) {
  const [open, setOpen] = useState(false);
  const [drinkingWater, setDrinkingWaterRaw] = usePoiDrinkingWater();
  // ULTRA: alte MTB-Custom-Kategorien (Cafés/Lunch/Bike/Beaches/Viewpoints)
  // wurden aus dem Panel entfernt — ersetzt durch die Overpass-Kategorien
  // entlang des Korridors. Nur Wasser bleibt als Custom-fähige Kategorie.
  // Wave 14cn: Battery-Station-Toggle. Nur für VL-Kunden sichtbar — Non-VL
  // sehen die Zeile gar nicht und können sie nicht ausschalten.
  const [batteryStation, setBatteryStationRaw] = usePoiBatteryStation();
  const { effective: isVl } = useVlMembership();

  // ULTRA: generische Overpass-Kategorien (fast_food, supermarket, …).
  const overpassToggles = useOverpassPoiToggles();

  // Wave 14ce: Bike-Only-Override (SOS-Page "Bike defekt") wird beendet
  // sobald der User manuell IRGENDEINEN POI-Toggle bewegt — sonst hätte
  // er ein "warum tut mein Toggle nichts"-Problem (Override hat Vorrang
  // gegenüber den persistierten Prefs). Wrapper um jeden Setter, der
  // VOR dem eigentlichen Schreiben den Override clearrt.
  const clearOverrideAndSet =
    (setter: (v: boolean) => void) => (v: boolean) => {
      poiOverrideBus.set(null);
      setter(v);
    };
  const setDrinkingWater = clearOverrideAndSet(setDrinkingWaterRaw);
  const setBatteryStation = clearOverrideAndSet(setBatteryStationRaw);

  // Wave 14cf-fix15: wenn das Panel sich öffnet, einen "POI refresh"-Event
  // feuern, damit MapPage seine adminPoi/userPoi-Caches neu lädt. Der
  // adminPoi-Cache ist 1h fresh — wenn der Admin gerade einen POI angelegt
  // hat, würde er ohne diesen Refresh erst nach 1h auf der Karte erscheinen.
  useEffect(() => {
    if (open) {
      window.dispatchEvent(new CustomEvent('adt:poi-refresh'));
    }
  }, [open]);

  // Badge-Zahl = Anzahl aktiver Kategorien. Battery-Station nur zählen
  // wenn der User VL-Kunde ist (sonst ist die Zeile unsichtbar).
  const activeCount =
    (drinkingWater ? 1 : 0) +
    OVERPASS_EXTRA_CATEGORIES.reduce(
      (sum, c) => sum + (overpassToggles[c].enabled ? 1 : 0),
      0,
    ) +
    (isVl && batteryStation ? 1 : 0);

  // Keine `uppercase`-Klasse im embedded-Modus — sonst rendert MapLibres
  // Tailwind-Transform "POIs" als "POIS" (User-Feedback 14d).
  const triggerClass = embedded
    ? 'relative h-[22px] px-1.5 rounded-md flex items-center gap-1 text-white/80 hover:text-white text-[11px] font-semibold leading-none tracking-wide transition-colors'
    : 'relative h-[26px] px-2 rounded-md shadow-sm flex items-center gap-1 border border-white/10 bg-black/60 hover:bg-black/75 active:bg-black/85 transition-colors text-white text-[12px] font-medium leading-none';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={triggerClass}
        aria-label="Show points of interest"
        title="Points of interest"
      >
        {/* Pin-Icon — passt jetzt für alle Kategorien (vorher war's 💧 weil
            es nur die eine Kategorie gab). */}
        <span aria-hidden="true" className={embedded ? 'text-[13px]' : ''}>📍</span>
        <span>POIs</span>
        {activeCount > 0 && (
          <span
            className={
              embedded
                ? 'absolute -top-1 -right-1.5 min-w-[14px] h-[14px] px-1 rounded-full bg-turbo-500 text-white text-[9px] font-bold flex items-center justify-center tabular-nums leading-none ring-1 ring-turbo-900'
                : 'absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-teal-500 text-white text-[10px] font-semibold flex items-center justify-center tabular-nums shadow'
            }
          >
            {activeCount}
          </span>
        )}
      </button>

      {open &&
        createPortal(
          <div
            // Wave 14cf-fix15: z-[60] (war z-50) damit das Panel garantiert
            // über dem Ride-Button (z-30) UND eventuellen anderen z-50-Elementen
            // wie dem Difficulty-Sheet liegt. Backdrop bg-black/80 (dunkler)
            // damit der Ride-Button hinter dem Backdrop nicht mehr durchscheint.
            //
            // Wave 14cf-fix18: Dialog zentriert + schmaler (max-w-[300px],
            // war max-w-sm). Backdrop respektiert die safe-area-insets damit
            // der X-Button auf iPhones mit Dynamic Island sichtbar bleibt.
            //
            // Wave 14cf-fix20: Modal via createPortal in document.body
            // gerendert. Vorher saß das Modal im Stacking-Context der
            // BottomBar-Pill, die per `backdrop-blur-md` einen eigenen
            // Containing-Block erzeugt — `fixed inset-0` wurde dadurch zur
            // Pill-Box statt zum Viewport, das Modal kollabierte zu einem
            // unsichtbaren Streifen im Pill. (Selbe Falle wie in
            // DifficultyLegend, dort schon mit createPortal gelöst.)
            className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center px-4"
            role="dialog"
            aria-modal="true"
            aria-label="Points of interest"
            onClick={() => setOpen(false)}
            style={{
              paddingTop: 'max(1rem, env(safe-area-inset-top))',
              paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
            }}
          >
            <div
              className="bg-turbo-900 border border-turbo-500/30 rounded-2xl p-4 w-full max-w-[300px] shadow-xl max-h-full overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-lg font-semibold flex-1">Points of interest</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 text-xl leading-none"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            {/* Wave 14ce: Erklär-Paragraph weg — die schlanken Zeilen mit
                inline-Expand erklären sich selbst (Toggle = on/off, ⓘ =
                Hint). Spart ~40 px vertikal. */}

            <div className="space-y-2">
              {/* Drinking-water-Zeile mit Cache-Status (OSM-Pull). */}
              <PoiCategoryRow
                category="drinking_water"
                hint="Fountains and public water taps (OSM + admin + user)."
                enabled={drinkingWater}
                onChange={setDrinkingWater}
                onClose={() => setOpen(false)}
              />

              {/* Wave 14cn / Wave 14co: Battery-Stations-Zeile.
                  - VL-Kunden sehen sie immer.
                  - Non-VL sehen sie nur wenn sie schon mal "Show in demo
                    mode" auf der AllDayTurboPage angeklickt haben (=
                    Pref steht auf true). Dann können sie's auch wieder
                    ausschalten. */}
              {(isVl || batteryStation) && (
                <PoiToggle
                  emoji="🔋"
                  label="Battery stations"
                  hint={
                    isVl
                      ? 'Vertical Lodge battery swap points — live SoC.'
                      : 'Demo stations — Vertical Lodge swap points. Read-only.'
                  }
                  enabled={batteryStation}
                  onChange={setBatteryStation}
                />
              )}

              {/* ULTRA: Overpass-Kategorien entlang des Korridors (OSM). Jede
                  mit Cache-Status (PoiCategoryRow), default aus. */}
              <div className="pt-2 mt-1 border-t border-white/10 text-[11px] uppercase tracking-wider text-white/40">
                Along the route (OSM)
              </div>
              {OVERPASS_EXTRA_CATEGORIES.map((cat) => (
                <PoiCategoryRow
                  key={cat}
                  category={cat}
                  hint={POI_CATEGORIES[cat].hint ?? ''}
                  enabled={overpassToggles[cat].enabled}
                  onChange={clearOverrideAndSet(overpassToggles[cat].set)}
                  onClose={() => setOpen(false)}
                />
              ))}
            </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * Wave 14ce: Slim Row mit inline-Expand.
 *
 * Vorher: jede Kategorie hatte einen fetten Pill-Button mit Emoji + Label
 * + 2-zeiligem Hint + Toggle (= ~72 px hoch × 6 Kategorien = 432 px) plus
 * der dazugehörige Cache-Status darunter. Das Panel füllte den ganzen
 * Bildschirm.
 *
 * Jetzt: jede Kategorie ist eine schlanke 44-px-Zeile mit
 *   Emoji · Label · ⓘ · Toggle
 * Tap auf ⓘ klappt einen kleinen Inline-Bereich mit Hint + Cache-Status
 * auf (kein eigenes Modal). Default collapsed, weniger Bla.
 */
function PoiCategoryRow({
  category,
  hint,
  enabled,
  onChange,
  onClose,
}: {
  category: PoiCategoryId;
  hint: string;
  enabled: boolean;
  onChange: (v: boolean) => void;
  /** Optional — Panel schließen bevor der User zum Offline-Tab navigiert. */
  onClose?: () => void;
}) {
  const def = POI_CATEGORIES[category];
  const [expanded, setExpanded] = useState(false);
  const [info, setInfo] = useState<{ count: number; oldestCachedAt: string | null } | null>(null);

  const reload = useCallback(async () => {
    try {
      const i = await getPoiCacheInfo(category);
      setInfo(i);
    } catch {
      setInfo({ count: 0, oldestCachedAt: null });
    }
  }, [category]);

  // Nur cache-info laden wenn expanded — spart fünf periodische
  // IDB-Reads im Hintergrund wenn der User nur kurz das Panel auf hat.
  useEffect(() => {
    if (!expanded) return;
    void reload();
    const id = window.setInterval(() => void reload(), 3000);
    return () => window.clearInterval(id);
  }, [expanded, reload]);

  const hasCache = info != null && info.count > 0;
  const ageText =
    info?.oldestCachedAt != null ? formatRelativeAge(info.oldestCachedAt) : null;

  return (
    <SlimPoiRow
      emoji={def.emoji}
      label={def.label}
      hint={hint}
      enabled={enabled}
      onChange={onChange}
      expanded={expanded}
      onToggleExpand={() => setExpanded((v) => !v)}
      extraInfo={
        info == null ? (
          <span className="text-white/40">Checking cache…</span>
        ) : hasCache ? (
          <span className="text-white/65">
            <span className="text-emerald-400">✓</span>{' '}
            <span className="tabular-nums">{info.count}</span> cached offline
            {ageText && <span className="text-white/40"> · {ageText}</span>}
          </span>
        ) : (
          <Link
            to="/offline"
            onClick={onClose}
            className="text-amber-300 underline decoration-dotted underline-offset-2 hover:text-amber-200"
          >
            Not downloaded — open Offline downloads
          </Link>
        )
      }
    />
  );
}

function formatRelativeAge(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(diffMs) || diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

/**
 * Wave 14ce: SlimPoiRow — der neue dünne Zeilen-Stil.
 * Eine Zeile: emoji · label · ⓘ · Toggle. Tap auf ⓘ klappt extra Info
 * (hint + cache-status) inline aus. Tap-on-Toggle clearrt zusätzlich die
 * Session-Override aus poiOverrideBus — damit ein manuelles
 * Mit-dem-Filter-Spielen sofort den "Bike defekt"-Override beendet (sonst
 * wäre die Map widerständig und User würde sich wundern warum sein
 * Toggle nichts tut).
 */
function SlimPoiRow({
  emoji,
  label,
  hint,
  enabled,
  onChange,
  expanded,
  onToggleExpand,
  extraInfo,
}: {
  emoji: string;
  label: string;
  hint: string;
  enabled: boolean;
  onChange: (v: boolean) => void;
  expanded: boolean;
  onToggleExpand: () => void;
  extraInfo?: React.ReactNode;
}) {
  return (
    <div
      className={`rounded-lg border transition-colors overflow-hidden ${
        enabled
          ? 'bg-teal-600/20 border-teal-500/50'
          : 'bg-turbo-700/40 border-transparent'
      }`}
    >
      <div className="flex items-stretch h-11">
        {/* Tap-Bereich für Label + emoji — toggelt die Visibility (= die
            "Hauptaktion" der Zeile). Lässt das ⓘ + den Toggle als
            separate Tap-Targets stehen. */}
        <button
          type="button"
          onClick={() => onChange(!enabled)}
          className="flex-1 flex items-center gap-2.5 px-3 text-left min-w-0"
          aria-pressed={enabled}
        >
          <span className="text-lg shrink-0">{emoji}</span>
          <span className="font-medium text-[14px] text-white truncate">
            {label}
          </span>
        </button>
        {/* ⓘ Info-Button — togglet expanded. Bewusst kompakt damit er die
            Zeile nicht aufbläht. */}
        <button
          type="button"
          onClick={onToggleExpand}
          className="px-2 text-white/55 hover:text-white/85 active:text-white"
          aria-expanded={expanded}
          aria-label={expanded ? `Hide info for ${label}` : `Show info for ${label}`}
          title="Info"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className={`transition-transform ${expanded ? 'rotate-180' : ''}`}
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        </button>
        {/* Toggle switch */}
        <button
          type="button"
          onClick={() => onChange(!enabled)}
          className="px-3 flex items-center"
          aria-pressed={enabled}
          aria-label={`${enabled ? 'Hide' : 'Show'} ${label} on map`}
        >
          <span
            className={`w-9 h-5 rounded-full relative transition-colors ${
              enabled ? 'bg-teal-500' : 'bg-white/20'
            }`}
            aria-hidden="true"
          >
            <span
              className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${
                enabled ? 'left-[18px]' : 'left-0.5'
              }`}
            />
          </span>
        </button>
      </div>
      {/* Inline-Expand: hint + extraInfo. Animiert wäre nice, aber bei
          kleinem Bildschirm reicht binary collapsed/expanded. */}
      {expanded && (
        <div className="px-3 pb-2 pt-1 border-t border-white/10 text-[12px] text-white/65 leading-snug space-y-1.5">
          <div>{hint}</div>
          {extraInfo && <div className="text-[11px]">{extraInfo}</div>}
        </div>
      )}
    </div>
  );
}

/**
 * Wave 14ce: PoiToggle ist jetzt ein dünner Wrapper um SlimPoiRow ohne
 * Cache-Status-Info — wird für die Custom-Kategorien benutzt (cafe,
 * pranzo, bike_shop, beach, viewpoint), die keinen eigenen IDB-Cache
 * haben (kommen jedes Mal frisch vom Server, 1h-Cached client-side).
 */
function PoiToggle({
  emoji,
  label,
  hint,
  enabled,
  onChange,
}: {
  emoji: string;
  label: string;
  hint: string;
  enabled: boolean;
  onChange: (v: boolean) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <SlimPoiRow
      emoji={emoji}
      label={label}
      hint={hint}
      enabled={enabled}
      onChange={onChange}
      expanded={expanded}
      onToggleExpand={() => setExpanded((v) => !v)}
    />
  );
}
